import { ErrorPolicy, gql, MutationHookOptions } from '@apollo/client';
import { useMutation } from 'platform/useMutation';

import {
  UpdateClosingTasks,
  UpdateClosingTasksVariables,
} from 'sellDirect/graphql/__generated__/UpdateClosingTasks';
import { ClosingTasksInput } from '__generated__/globalTypes';
import { useCallback, useEffect } from 'react';
import { useObservability } from 'shared/helpers/observability';
import { useOfferContext } from 'sellDirect/OfferContext';

export const ClosingTaskSubmissionEventName = 'closing-task-submission-success';

// We don't care about the response, so just putting __typename to make GraphQL happy.
const UpdateClosingTasksMutation = gql`
  mutation UpdateClosingTasks($webOfferToken: String!, $tasks: ClosingTasksInput!) {
    seller {
      updateClosingTasks(webOfferToken: $webOfferToken, tasks: $tasks) {
        __typename
      }
    }
  }
`;
/** TODO: Add documentation */
export const useUpdateClosingTasks = (
  label: string,
  options?: {
    onCompleted?: MutationHookOptions<
      UpdateClosingTasks,
      UpdateClosingTasksVariables
    >['onCompleted'];
    onError?: MutationHookOptions<UpdateClosingTasks, UpdateClosingTasksVariables>['onError'];
    errorPolicy?: ErrorPolicy;
  },
) => {
  const { offer } = useOfferContext();
  const { trackEvent, trackError } = useObservability();

  const onUpdateSuccess = (data: UpdateClosingTasks) => {
    trackEvent(ClosingTaskSubmissionEventName, label);
    options?.onCompleted && options?.onCompleted(data);
  };

  const [update, { loading, error }] = useMutation<UpdateClosingTasks, UpdateClosingTasksVariables>(
    UpdateClosingTasksMutation,
    {
      onCompleted: onUpdateSuccess,
      onError: options?.onError,
      awaitRefetchQueries: true,
      errorPolicy: options?.errorPolicy,
      fetchPolicy: 'no-cache',
      refetchQueries: () => ['GetSellDirect'],
    },
  );

  const updateClosingTasks = useCallback(
    (tasks: ClosingTasksInput) => {
      if (!offer?.leadToken) {
        trackError('missing-web-offer-token');
        return;
      }
      update({
        variables: {
          webOfferToken: offer.leadToken,
          tasks,
        },
      }).catch((error) => {
        trackEvent('error', label, {
          tasks,
          error,
        });
      });
    },
    [update, offer?.leadToken, trackEvent, label, trackError],
  );

  useEffect(() => {
    if (error) {
      trackEvent('error', label, {
        error,
      });
    }
  }, [trackEvent, error, label]);

  return { updateClosingTasks, loading, error };
};
